"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.algoliaClient = void 0;
const profanity_1 = require("../services/profanity");
const algoliasearch_1 = require("algoliasearch");
const ErrorMessage_1 = require("../constants/ErrorMessage");
const apiClient_1 = require("./apiClient");
/**
 * Creates an Algolia client with additional methods.
 * @param {string} appId - Algolia Application ID.
 * @param {string} apiKey - Algolia API Key.
 * @param {Object} [options] - Optional parameters.
 * @param {string} [options.indexName] - Default index name to use.
 * @returns {SearchClient} A SearchClient object with additional methods.
 */
const algoliaClient = (appId, apiKey, options) => {
    const client = (0, algoliasearch_1.algoliasearch)(appId, apiKey);
    const recommendClient = client.initRecommend();
    const insights = client.initInsights({ region: options?.region || "us" });
    return {
        /**
         * Searches for stores by latitude and longitude.
         * @typedef {Object} StoreByLatLngParamsType
         * @throws {Error} If no index name is provided.
         * @returns {Promise<Object>} Search results.
         */
        storeSearchByLatLng: async (params) => {
            const { lat, lng, hitsPerPage = 20, page = 0, query = "", indexName = "", ...rest } = params;
            const index = indexName || options?.indexName;
            if (!index) {
                throw new Error(ErrorMessage_1.ErrorMessages.INVALID_INDEX);
            }
            return client.searchSingleIndex({
                indexName: index,
                searchParams: {
                    query: query,
                    aroundLatLng: lat + ", " + lng,
                    hitsPerPage,
                    page,
                    aroundRadius: "all",
                    ...rest,
                },
            });
        },
        /**
         * Fetches popular searches from multiple indices.
         * @param {Object} params - Parameters for fetching popular searches.
         * @param {string[]} params.indexNames - List of index names.
         * @param {string} params.analyticsApiKey - API key for analytics.
         * @param {number} [params.limit=10] - Number of popular searches to return.
         * @returns {Promise<Array<{search: string, count: number}>>} Array of popular searches.
         */
        popularSearch: async ({ indexNames, analyticsApiKey, limit = 10, ...rest }) => {
            const api = (0, apiClient_1.apiClient)(appId, analyticsApiKey || apiKey);
            let searches = [];
            const hasSearches = new Set();
            const data = await Promise.allSettled([
                ...indexNames.map((indexName) => api.popularSearch({ ...rest, indexName, limit: limit * 2 })),
            ]);
            data.forEach((item) => {
                if (item.status == "fulfilled") {
                    searches = searches.concat(item.value?.data?.searches || []);
                }
            });
            return [...searches]
                // Descending Order
                .sort((a, b) => b.count - a.count)
                // Remove empty, duplicate or profanity searches
                .filter((item) => {
                if (!item.search ||
                    hasSearches.has(item.search) ||
                    (0, profanity_1.hasProfanity)(item.search)) {
                    return false;
                }
                hasSearches.add(item.search);
                return true;
            })
                // Return top specified only.
                .slice(0, limit);
        },
        /**
         * Retrieves details of multiple objects from different/same indexes.
         * @param {Array<Object>} params - List of object requests.
         * @returns {Promise<Object>} Object details.
         */
        getObjectDetails: async (params) => {
            return client.getObjects({ requests: params });
        },
        /**
         * Retrieves details of a single object.
         * @param {Object} params - Object request parameters.
         * @returns {Promise<Object>} Object details.
         */
        getObjectDetail: async (params) => {
            return client.getObject(params);
        },
        /**
         * To Search from a multi-indexes.
         * @param {Array<Object>} searchParams - Search parameters for each index.
         * @param {string} strategy - Strategy for search execution.
         * @returns {Promise<Object>} Multi-index search results.
         */
        multiIndexSearch: async (searchParams, strategy) => {
            return client.search({ requests: searchParams, strategy });
        },
        /**
         * To Search from a single.
         * @param {String} params.indexName - Algolia target index
         * @param {Object} params.searchParams - Search params like filter, query etc.
         * @param {Object} reqOption - Optional reqOptions like header etc.
         * @returns {Promise<Object>} Multi-index search results.
         */
        search: async (params, reqOption) => {
            return client.searchSingleIndex(params, reqOption);
        },
        analytics: insights,
        getRecommendations: recommendClient.getRecommendations,
    };
};
exports.algoliaClient = algoliaClient;
